import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  Avatar,
  Divider,
  Grid,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Chip,
  Paper,
  Container,
  Skeleton,
} from '@mui/material';
import { useNavbar } from '../context/NavbarContext';
import { getUserFromDynamoDB, updateUserInDynamoDB } from '../api/apiService';
import { useSelector } from 'react-redux';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import EmailIcon from '@mui/icons-material/Email';
import BadgeIcon from '@mui/icons-material/Badge';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { styled, alpha } from '@mui/material/styles';

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  padding: {
    xs: theme.spacing(2),
    sm: theme.spacing(3),
    md: theme.spacing(4)
  },
  borderRadius: '16px',
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.palette.mode === 'dark' ? '0px 8px 28px rgba(0,0,0,0.35)' : '0px 4px 30px rgba(0, 0, 0, 0.05)',
  border: `1px solid ${theme.palette.divider}`,
  transition: 'all 0.3s ease',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  '&:hover': {
    boxShadow: theme.palette.mode === 'dark' ? '0px 12px 40px rgba(0,0,0,0.45)' : '0px 8px 40px rgba(0, 0, 0, 0.08)',
    transform: 'translateY(-4px)',
  },
}));

const InfoItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1),
  borderRadius: '8px',
  width: '100%',
  overflow: 'hidden',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.06 : 0.04),
  },
  '& .MuiTypography-root': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: 'calc(100% - 40px)', // Account for icon width and gap
    fontSize: {
      xs: '0.875rem',
      sm: '1rem',
      md: '1rem'
    },
    fontWeight: '500',
    letterSpacing: '0.3px',
    color: theme.palette.text.primary,
  }
}));

const ProfilePage = () => {
  const { isNavbarOpen } = useNavbar();
  const currentUserEmail = useSelector((state) => state.auth.user?.email);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    if (!currentUserEmail) {
      setLoading(false);
      return;
    }

    const fetchUserData = async () => {
      try {
        const data = await getUserFromDynamoDB(currentUserEmail);

        if (data) {
          setUserData(data);
          setEditedData(data);
        } else {
          console.log('User not found in DynamoDB');
        }
      } catch (error) {
        console.error('Error fetching user data from DynamoDB:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [currentUserEmail]);

  const handleSave = async () => {
    try {
      const updated = await updateUserInDynamoDB(currentUserEmail, editedData);
      setUserData(updated);
      setIsEditing(false);
      setOpenDialog(false);
    } catch (error) {
      console.error('Error updating user data in DynamoDB:', error);
    }
  };

  const handleChange = (field) => (event) => {
    setEditedData({
      ...editedData,
      [field]: event.target.value,
    });
  };

  const handleCancel = () => {
    setEditedData(userData);
    setIsEditing(false);
    setOpenDialog(false);
  };

  return (
  <Box sx={{ 
      display: 'flex', 
      minHeight: '100vh', 
      width: '100%', 
      background: (theme) => theme.palette.background.default,
      margin: 0,
      padding: 0,
      overflow: 'auto',
    }}>
      <Container
        disableGutters
        sx={{
          minHeight: '100vh',
          width: '100%',
          flexGrow: 1,
          padding: {
            xs: '12px',
            sm: '16px',
            md: '20px',
            lg: '24px',
            xl: '32px'
          },
          marginLeft: {
            xs: isNavbarOpen ? '240px' : '70px',
            sm: isNavbarOpen ? '240px' : '70px',
            md: isNavbarOpen ? '240px' : '70px',
            lg: isNavbarOpen ? '240px' : '70px',
            xl: isNavbarOpen ? '240px' : '70px'
          },
          marginTop: {
            xs: '65px',
            sm: '64px',
            md: '64px',
            lg: '64px',
            xl: '64px'
          },
          transition: 'margin-left 0.3s, padding 0.3s',
          position: 'relative',
          maxWidth: '100% !important',
          overflow: 'auto',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: { xs: 2, sm: 3 } }}>
          <Typography 
            variant="h4" 
            sx={{ 
              color: (theme) => theme.palette.text.primary,
              letterSpacing: '-0.5px',
              fontSize: {
                xs: '1.5rem',
                sm: '1.75rem',
                md: '2rem',
                lg: '2.25rem',
                xl: '2.5rem'
              },
              fontWeight: '600'
            }}
          >
            Profile
          </Typography>
          <Typography variant="body2" sx={{ color: (theme) => theme.palette.text.secondary }}>View and edit your profile.</Typography>
        </Box>

        <Grid container spacing={{ xs: 2, sm: 3, md: 3 }}>
          {/* Profile Card */}
          <Grid item xs={12} md={4} sx={{ order: { xs: 0, md: 0 } }}>
            {loading ? (
              <Paper elevation={0} sx={{ p: 2, borderRadius: '16px', border: (theme) => `1px solid ${theme.palette.divider}`, backgroundColor: (theme) => theme.palette.background.paper }}>
                <Skeleton variant="circular" width={120} height={120} sx={{ mx: 'auto', my: 2 }} />
                <Skeleton variant="text" height={32} width="60%" sx={{ mx: 'auto' }} />
                <Skeleton variant="rounded" height={28} width={100} sx={{ mx: 'auto', mt: 1 }} />
                <Divider sx={{ my: 2 }} />
                {[...Array(4)].map((_, i) => (
                  <Skeleton key={i} variant="rounded" height={36} sx={{ mb: 1 }} />
                ))}
                <Skeleton variant="rounded" height={36} sx={{ mt: 1 }} />
              </Paper>
            ) : (
            <StyledCard sx={{ position: { md: 'sticky' }, top: { md: '84px' } }}>
              <Box sx={{ 
                textAlign: 'center', 
                mb: {
                  xs: 2,
                  sm: 2,
                  md: 3
                },
                flexShrink: 0,
                mx: {
                  xs: 2,
                  sm: 3,
                  md: 4
                }
              }}>
                <Avatar
                  src={userData?.profilePicture || '/default-avatar.png'}
                  sx={{ 
                    width: {
                      xs: 100,
                      sm: 110,
                      md: 120
                    },
                    height: {
                      xs: 100,
                      sm: 110,
                      md: 120
                    },
                    margin: 'auto', 
                    border: (theme) => `3px solid ${theme.palette.primary.main}`,
                    boxShadow: (theme) => `0 4px 20px ${alpha(theme.palette.primary.main, 0.2)}`,
                  }}
                />
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: '600', 
                    mt: {
                      xs: 1,
                      sm: 1.5,
                      md: 2
                    }, 
                    color: (theme) => theme.palette.text.primary,
                    fontSize: {
                      xs: '1.25rem',
                      sm: '1.5rem',
                      md: '1.75rem'
                    },
                    letterSpacing: '0.3px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {userData?.name || 'User Name'}
                </Typography>
                <Chip 
                  label={userData?.role || 'Role'} 
                  color="primary" 
                  sx={{ 
                    mt: 1,
                    fontSize: {
                      xs: '0.75rem',
                      sm: '0.875rem',
                      md: '0.875rem'
                    },
                    fontWeight: '500',
                    letterSpacing: '0.3px',
                    maxWidth: '100%',
                    '& .MuiChip-label': {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }
                  }}
                />
              </Box>
              <Divider sx={{ my: { xs: 1.5, sm: 2, md: 2 }, flexShrink: 0, mx: { xs: 2, sm: 3, md: 4 } }} />
              <Stack 
                spacing={{ xs: 1.5, sm: 2, md: 2 }}
                sx={{
                  flexGrow: 1,
                  minHeight: 0,
                  overflow: 'auto',
                  mx: {
                    xs: 2,
                    sm: 3,
                    md: 4
                  },
                  my: {
                    xs: 1,
                    sm: 1.5,
                    md: 2
                  }
                }}
              >
                <InfoItem>
                  <EmailIcon color="primary" sx={{ flexShrink: 0 }} />
                  <Typography>
                    {userData?.email}
                  </Typography>
                </InfoItem>
                <InfoItem>
                  <BadgeIcon color="primary" sx={{ flexShrink: 0 }} />
                  <Typography>
                    {userData?.role}
                  </Typography>
                </InfoItem>
                <InfoItem>
                  <PhoneIcon color="primary" sx={{ flexShrink: 0 }} />
                  <Typography>
                    {userData?.phone || 'Not provided'}
                  </Typography>
                </InfoItem>
                <InfoItem>
                  <LocationOnIcon color="primary" sx={{ flexShrink: 0 }} />
                  <Typography>
                    {userData?.location || 'Not provided'}
                  </Typography>
                </InfoItem>
              </Stack>
              <Button
                startIcon={<EditIcon />}
                variant="outlined"
                color="primary"
                sx={{ 
                  mt: {
                    xs: 2,
                    sm: 2.5,
                    md: 3
                  },
                  fontSize: {
                    xs: '0.875rem',
                    sm: '1rem',
                    md: '1rem'
                  },
                  fontWeight: '500',
                  letterSpacing: '0.3px',
                  flexShrink: 0,
                  mx: {
                    xs: 2,
                    sm: 3,
                    md: 4
                  },
                  mb: {
                    xs: 1,
                    sm: 1.5,
                    md: 2
                  }
                }}
                aria-label="Edit profile"
                onClick={() => setOpenDialog(true)}
              >
                Edit Profile
              </Button>
            </StyledCard>
            )}
          </Grid>

          {/* Activity Card */}
          <Grid item xs={12} md={8} sx={{ order: { xs: 1, md: 0 } }}>
            {loading ? (
              <Paper elevation={0} sx={{ p: 2, borderRadius: '16px', border: (theme) => `1px solid ${theme.palette.divider}`, backgroundColor: (theme) => theme.palette.background.paper }}>
                <Skeleton variant="text" height={32} width="40%" sx={{ mb: 2 }} />
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} variant="rounded" height={48} sx={{ mb: 1 }} />
                ))}
              </Paper>
            ) : (
            <StyledCard>
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: {
                    xs: 2,
                    sm: 2.5,
                    md: 3
                  }, 
                  color: (theme) => theme.palette.text.primary, 
                  fontWeight: '600',
                  letterSpacing: '0.3px',
                  fontSize: {
                    xs: '1.25rem',
                    sm: '1.5rem',
                    md: '1.75rem'
                  },
                  flexShrink: 0,
                  mx: {
                    xs: 2,
                    sm: 3,
                    md: 4
                  }
                }}
              >
                Recent Activity
              </Typography>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: {
                    xs: 1.5,
                    sm: 2,
                    md: 2
                  }, 
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? alpha('#FFFFFF', 0.03) : '#F8FAFF',
                  borderRadius: '12px',
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  flexGrow: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 0,
                  overflow: 'auto',
                  mx: {
                    xs: 2,
                    sm: 3,
                    md: 4
                  },
                  my: {
                    xs: 1,
                    sm: 1.5,
                    md: 2
                  }
                }}
              >
                <Typography 
                  color="text.secondary"
                  sx={{
                    fontSize: {
                      xs: '0.875rem',
                      sm: '1rem',
                      md: '1rem'
                    },
                    fontWeight: '500',
                    letterSpacing: '0.3px'
                  }}
                >
                  No recent activity to display.
                </Typography>
              </Paper>
            </StyledCard>
            )}
          </Grid>

          {/* Account Information Card */}
          <Grid item xs={12} md={8} sx={{ order: { xs: 2, md: 1 } }}>
            {loading ? (
              <Paper elevation={0} sx={{ p: 2, borderRadius: '16px', border: (theme) => `1px solid ${theme.palette.divider}`, backgroundColor: (theme) => theme.palette.background.paper }}>
                <Skeleton variant="text" height={28} width="35%" sx={{ mb: 2 }} />
                {[...Array(6)].map((_, i) => (
                  <Skeleton key={i} variant="rounded" height={40} sx={{ mb: 1 }} />
                ))}
              </Paper>
            ) : (
            <StyledCard>
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: { xs: 2, sm: 2.5, md: 3 }, 
                  color: (theme) => theme.palette.text.primary, 
                  fontWeight: '600',
                  letterSpacing: '0.3px',
                  fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
                  mx: { xs: 2, sm: 3, md: 4 }
                }}
              >
                Account Information
              </Typography>
              <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, pb: { xs: 2, sm: 3, md: 4 } }}>
                <Grid container spacing={2}>
                  {[{ label: 'Name', value: userData?.name || '-' },
                    { label: 'Email', value: userData?.email || '-' },
                    { label: 'Role', value: userData?.role || '-' },
                    { label: 'Phone', value: userData?.phone || 'Not provided' },
                    { label: 'Location', value: userData?.location || 'Not provided' }]
                    .map((row) => (
                      <Grid key={row.label} item xs={12} sm={6}>
                        <Box sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                          p: 1.5,
                          borderRadius: '10px',
                          border: (theme) => `1px solid ${theme.palette.divider}`,
                          backgroundColor: (theme) => theme.palette.background.paper
                        }}>
                          <Typography variant="caption" sx={{ color: (theme) => theme.palette.text.secondary }}>{row.label}</Typography>
                          <Typography variant="body1" sx={{ color: (theme) => theme.palette.text.primary, fontWeight: 500 }}>
                            {row.value}
                          </Typography>
                        </Box>
                      </Grid>
                  ))}
                </Grid>
              </Box>
            </StyledCard>
            )}
          </Grid>
        </Grid>

        {/* Edit Profile Dialog */}
        <Dialog 
          open={openDialog} 
          onClose={handleCancel} 
          maxWidth="sm" 
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '12px',
              m: { xs: 2, sm: 4 },
              width: {
                xs: '90%',
                sm: '500px',
                md: '600px'
              }
            }
          }}
        >
          <DialogTitle sx={{ 
            fontSize: {
              xs: '1.25rem',
              sm: '1.5rem',
              md: '1.75rem'
            }
          }}>
            Edit Profile
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 2 }}>
              <TextField
                label="Name"
                value={editedData?.name || ''}
                onChange={handleChange('name')}
                fullWidth
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: {
                      xs: '0.875rem',
                      sm: '1rem',
                      md: '1rem'
                    }
                  },
                  '& .MuiInputBase-root': {
                    fontSize: {
                      xs: '0.875rem',
                      sm: '1rem',
                      md: '1rem'
                    }
                  }
                }}
              />
              <TextField
                label="Phone"
                value={editedData?.phone || ''}
                onChange={handleChange('phone')}
                fullWidth
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: {
                      xs: '0.875rem',
                      sm: '1rem',
                      md: '1rem'
                    }
                  },
                  '& .MuiInputBase-root': {
                    fontSize: {
                      xs: '0.875rem',
                      sm: '1rem',
                      md: '1rem'
                    }
                  }
                }}
              />
              <TextField
                label="Location"
                value={editedData?.location || ''}
                onChange={handleChange('location')}
                fullWidth
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: {
                      xs: '0.875rem',
                      sm: '1rem',
                      md: '1rem'
                    }
                  },
                  '& .MuiInputBase-root': {
                    fontSize: {
                      xs: '0.875rem',
                      sm: '1rem',
                      md: '1rem'
                    }
                  }
                }}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={handleCancel}
              sx={{
                fontSize: {
                  xs: '0.875rem',
                  sm: '1rem',
                  md: '1rem'
                }
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSave} 
              variant="contained" 
              startIcon={<SaveIcon />}
              sx={{
                fontSize: {
                  xs: '0.875rem',
                  sm: '1rem',
                  md: '1rem'
                }
              }}
            >
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default ProfilePage;
